import { EstimateSource } from '@lune-fe/dapi-models'
import { useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { ButtonNew, MainLayoutContainerNew, Modal, Text } from '@lune-fe/lune-ui-lib'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { useState } from 'react'

import { downloadBusinessEstimateCsv, downloadLogisticsEstimateCsv } from 'endpoints/dapi'
import useIsTestMode from 'hooks/useIsTestMode'
import useTitle from 'hooks/useTitle'
import { assertType } from 'utils'
import { EmissionCalculationFilters, EmissionCalculations } from 'views/CalculateEmissions'
import NewCalculationDropdown from 'views/CalculateEmissions/NewCalculationDropdown'
import { NavigationSource } from 'views/CalculateEmissions/Shared/BackButtonWithFromState'

const CalculateEmissions = () => {
    useTitle('Emissions Calculations')
    const isTestMode = useIsTestMode()
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()
    const [filters, setFilters] = useState<EmissionCalculationFilters | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const handleDownloadCsv = async () => {
        if (!filters) {
            // We expect at the very least a `calculationType` to be set
            return
        }

        let result

        if (filters.calculationType === 'Logistics') {
            /* The endpoint expects a `true/false` parameters, if both values are set in the filters, this is
                the same as passing no parameters to the endpoint */
            const isShipment =
                filters.shipmentStatuses.length === 1
                    ? filters.shipmentStatuses[0] === 'shipment'
                    : undefined

            // Expand "import" into individual import types
            const expandedSources = filters.sources.flatMap((source): EstimateSource[] => {
                if (source === 'import') {
                    return ['csv', 'ecotransit_csv', 'ecotransit', 'rfq']
                }
                if (source !== 'dashboard' && source !== 'api') {
                    // TODO: Improve types in EmissionCalculations component
                    throw new Error(`Unexpected estimate source: ${source}`)
                }
                return [source]
            })

            result = await downloadLogisticsEstimateCsv({
                accountIds:
                    filters.selectedAccountIds.length > 0 ? filters.selectedAccountIds : undefined,
                from: filters.dateRange?.startDate,
                through: filters.dateRange?.endDate,
                isShipment,
                source: expandedSources.length > 0 ? expandedSources : undefined,
            })
        } else {
            assertType<'Spend'>(filters.calculationType)
            result = await downloadBusinessEstimateCsv({
                accountIds:
                    filters.selectedAccountIds.length > 0 ? filters.selectedAccountIds : undefined,
                from: filters.dateRange?.startDate,
                through: filters.dateRange?.endDate,
                estimateTypes: filters.types.length > 0 ? filters.types : undefined,
            })
        }

        if (result.isOk() && result.value === 204) {
            snackbar(
                "Your CSV is being generated. You'll receive an email with a download link shortly",
                { variant: 'success' },
            )
        } else {
            snackbar('Failed to begin estimate download. Please try again.', {
                variant: 'error',
                error: result.isErr() ? result.error : undefined,
            })
        }

        setIsModalOpen(false)
    }

    return (
        <MainLayoutContainerNew
            isTestMode={!!isTestMode}
            title={'Emissions Calculations'}
            subtitle={'View and filter all individual emissions calculations'}
            primaryButton={<NewCalculationDropdown from={NavigationSource.CALCULATE_EMISSIONS} />}
            secondaryButton={
                <ButtonNew
                    variant={'secondary'}
                    leadingIcon={<FileDownloadOutlinedIcon />}
                    onClick={() => setIsModalOpen(true)}
                >
                    Download CSV
                </ButtonNew>
            }
            sx={{
                height: isTestMode ? `calc(100vh - 24px)` : '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '.main-layout-container__header': {
                    top: 0,
                },
                '.main-layout-container__body': {
                    overflowY: 'hidden',
                },
            }}
        >
            <EmissionCalculations onFiltersChange={setFilters} />
            <Modal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                header={
                    <Text variant="h5" fontWeight={600}>
                        Prepare CSV download
                    </Text>
                }
                subheader={
                    <Text variant="body1" sx={{ textAlign: 'center' }} color="text.secondary">
                        {
                            "Generating your CSV may take a few minutes to several hours depending on the volume of estimates. You'll receive an email with a download link once it's ready."
                        }
                    </Text>
                }
                actions={
                    <>
                        <ButtonNew
                            sx={{ width: '140px' }}
                            variant="secondary"
                            onClick={() => setIsModalOpen(false)}
                        >
                            Cancel
                        </ButtonNew>
                        <ButtonNew
                            sx={{ width: '140px' }}
                            variant="primary"
                            onClick={handleDownloadCsv}
                        >
                            Confirm
                        </ButtonNew>
                    </>
                }
            />
        </MainLayoutContainerNew>
    )
}

export default CalculateEmissions
